import { trackEvent, TrackingEvents } from "@sembark-travel/tracking"
import { useMemo, useSyncExternalStore } from "react"
import config from "../config"

let swRegistration: undefined | ServiceWorkerRegistration = undefined

export type Platform = "mac" | "windows" | "mobile" | "pwa" | "unknown"

type VersionUpdateParams = {
	currentVersion: string
	latestVersion: string
	platform: Platform
}

type NavigatorWithUAData = Navigator & {
	userAgentData: { platform: string }
}
export function setCurrentSWRegistration(
	registration: ServiceWorkerRegistration
) {
	swRegistration = registration
}

let state = {
	isUpdating: false,
	updateAvailable: false,
	updateRequiredWithMinimumVersion: undefined as undefined | string,
	isDownloadingUpdate: false,
}

const listeners: Array<() => void> = []

function subscribe(fn: () => void) {
	listeners.push(fn)
	return () => {
		const index = listeners.indexOf(fn)
		if (index >= 0) {
			listeners.splice(index, 1)
		}
	}
}

function setState(newState: Partial<typeof state>) {
	state = {
		...state,
		...newState,
	}
	listeners.forEach((l) => l())
}

function getState() {
	return state
}

export function setAppUpdateAvailable(
	registration: undefined | ServiceWorkerRegistration
) {
	trackEvent(TrackingEvents.web_app_update_available)
	swRegistration = registration
	setState({ updateAvailable: true })
}

export function showAppUpdateRequiredNotification(version: string) {
	if (state.updateRequiredWithMinimumVersion !== version) {
		setTimeout(() => {
			if (state.updateRequiredWithMinimumVersion !== version) {
				setState({ updateRequiredWithMinimumVersion: version })
			}
		}, 3000)
	}
}

async function updateApp() {
	if (!state.updateAvailable) {
		setState({ isDownloadingUpdate: true })
		// try to get the new worker
		if (swRegistration) {
			await swRegistration.update()
			setTimeout(() => {
				// reload the page if unable to update
				if (!state.updateAvailable) {
					window.alert(
						"Unable to auto-download the update. Please refresh the page to download the update"
					)
					window.location.reload()
				}
			}, 5000)
		} else {
			window.location.reload()
		}
		return
	}
	setState({ isUpdating: true })
	if (swRegistration) {
		trackEvent(TrackingEvents.web_app_update_clicked)
		swRegistration.waiting?.postMessage({ type: "SKIP_WAITING" })
	} else {
		window.alert(
			"The update was not correctly installed. Please close and reopen the tabs."
		)
		window.location.reload()
	}
}

export function detectPlatform(): Platform {
	let isPWA = false

	try {
		isPWA = window.sessionStorage.getItem("app_opened_via") === "pwa"
	} catch (e) {
		console.error(e)
	}
	// PWA check
	if (isPWA) {
		return "pwa"
	}

	const nav = navigator as NavigatorWithUAData
	const ua = navigator.userAgent.toLowerCase()
	const platform = nav.userAgentData?.platform?.toLowerCase() ?? ""

	// userAgentData (modern browsers)
	if (platform) {
		if (platform === "macos") return "mac"
		if (platform === "windows") return "windows"
		if (platform === "android" || platform === "ios") return "mobile"
	}

	//  Fallback (UA-based)
	const isIOS =
		/iphone|ipad|ipod/.test(ua) ||
		(platform === "macintel" && navigator.maxTouchPoints > 1)

	if (isIOS || /android/.test(ua)) return "mobile"
	if (platform.includes("mac")) return "mac"
	if (platform.includes("win")) return "windows"

	return "unknown"
}
export function getUpdateMessage({
	currentVersion,
	latestVersion,
	platform,
}: VersionUpdateParams) {
	const shortcuts = {
		mac: "cmd + shift + r",
		windows: "ctrl + shift + r",
	}

	switch (platform) {
		case "mac":
			return {
				heading: `Update Available: Version ${latestVersion} - Press \`${shortcuts.mac}\` to Refresh`,
				body: `Your current version (${currentVersion}) needs updating. Click “Download Now” or press ${shortcuts.mac} to update. If you are unable to update, please close all Sembark browser tabs and reopen them.`,
			}

		case "windows":
			return {
				heading: `Update Available: Version ${latestVersion} - Press \`${shortcuts.windows}\` to Refresh`,
				body: `Your current version (${currentVersion}) needs updating. Click "Download Now" or press ${shortcuts.windows} to update. If you are unable to update, please close all Sembark browser tabs and reopen them.`,
			}

		case "mobile":
			return {
				heading: `Update Available: Version ${latestVersion} - Refresh Page to Update`,
				body: `Your current version (${currentVersion}) needs updating. Click "Download Now" or refresh the page to update. If you are unable to update, please close all Sembark browser tabs and reopen them.`,
			}

		case "pwa":
			return {
				heading: `Update Available: Version ${latestVersion} - Update and Restart App`,
				body: `Your current version (${currentVersion}) needs updating. Click "Download Now", then close and reopen the app to update. If you are unable to update, please close and restart the app completely.`,
			}

		default:
			return {
				heading: `Update Available: Version ${latestVersion}`,
				body: `Your current version (${currentVersion}) needs updating. Please refresh or restart the app to update.`,
			}
	}
}

export function useAppInstaller() {
	const { updateAvailable, updateRequiredWithMinimumVersion, ...props } =
		useSyncExternalStore(subscribe, getState)

	const platform = useMemo(() => detectPlatform(), [])

	const updateMessageForMinimumVersionRequired = useMemo(() => {
		if (!updateRequiredWithMinimumVersion) return null

		return getUpdateMessage({
			currentVersion: config.appVersion,
			latestVersion: updateRequiredWithMinimumVersion,
			platform,
		})
	}, [updateRequiredWithMinimumVersion, platform])
	return {
		isUpdateAvailable: updateAvailable,
		updateApp,
		updateMessageForMinimumVersionRequired,
		updateRequiredWithMinimumVersion,
		...props,
	}
}
